import { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { YStack, Text, Input, styled } from 'tamagui'
import type { Deck, WorkshopAccessResult } from '../../content/decks/types'
import { getDecksByWorkshop, getDeck } from '../../content/decks'

interface DeckGateProps {
  workshopSlug?: string
  onAuthenticated: (result: {
    deck: Deck
    attendeeName?: string
    accessCodeId: string
    viewId: string
    workshopSlug: string
  }) => void
  onClose: () => void
  requestedDeckType?: string
}

const Overlay = styled(YStack, {
  name: 'DeckGateOverlay',
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(10, 10, 10, 0.95)',
  style: { zIndex: 10001 },
})

const CodeInput = styled(Input, {
  name: 'DeckGateInput',
  width: '100%',
  backgroundColor: '$cardBackground',
  borderWidth: 1,
  borderColor: '$border',
  borderRadius: '$4',
  paddingHorizontal: '$5',
  paddingVertical: '$4',
  color: '$textPrimary',
  fontFamily: '$mono',
  fontSize: 16,
  textAlign: 'center',
  outlineWidth: 0,
  focusStyle: {
    borderColor: '$orangePrimary',
    outlineWidth: 0,
  },
})

const PickerCard = styled(YStack, {
  name: 'DeckPickerCard',
  padding: '$4',
  borderRadius: '$3',
  borderWidth: 1,
  borderColor: '$border',
  backgroundColor: '$cardBackground',
  cursor: 'pointer',
  hoverStyle: {
    borderColor: '$orangePrimary',
    backgroundColor: '#FF630010',
  },
  variants: {
    active: {
      true: {
        borderColor: '$orangePrimary',
        backgroundColor: '#FF630010',
      },
    },
  } as const,
})

export function DeckGate({
  workshopSlug,
  onAuthenticated,
  onClose,
  requestedDeckType,
}: DeckGateProps) {
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [pickerMode, setPickerMode] = useState(false)
  const [pickerIndex, setPickerIndex] = useState(0)
  const [authResult, setAuthResult] = useState<WorkshopAccessResult | null>(null)
  const [resolvedSlug, setResolvedSlug] = useState(workshopSlug || '')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const tryFocus = () => {
      if (inputRef.current) {
        inputRef.current.focus()
        return
      }
      const el = document.querySelector<HTMLInputElement>('[data-gate-input]')
      el?.focus()
    }
    const timer = setTimeout(tryFocus, 100)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    if (!error) return
    const t = setTimeout(() => setError(''), 1500)
    return () => clearTimeout(t)
  }, [error])

  const openDeck = useCallback(
    async (deck: Deck, result: WorkshopAccessResult, slug: string) => {
      try {
        const viewRes = await fetch('/api/deck/view', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accessCodeId: result.accessCodeId,
            deckId: deck.id,
          }),
        })
        const viewData = await viewRes.json()

        onAuthenticated({
          deck,
          attendeeName: result.attendeeName,
          accessCodeId: result.accessCodeId!,
          viewId: viewData.viewId,
          workshopSlug: slug,
        })
      } catch {
        setError('Failed to start session')
        setLoading(false)
      }
    },
    [onAuthenticated]
  )

  const handleSubmit = useCallback(async () => {
    if (!code.trim() || loading) return
    setLoading(true)
    setError('')

    try {
      const body: Record<string, string> = { code: code.trim() }
      if (workshopSlug) body.workshopSlug = workshopSlug

      const res = await fetch('/api/deck/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const result: WorkshopAccessResult = await res.json()

      if (!result.valid || !result.accessCodeId || !result.workshopSlug) {
        setError('Invalid code')
        setLoading(false)
        return
      }

      const slug = result.workshopSlug
      setResolvedSlug(slug)

      const availableDecks = getDecksByWorkshop(slug)

      if (requestedDeckType || availableDecks.length <= 1) {
        const deckType =
          requestedDeckType || availableDecks[0]?.type || 'main'
        const deck = getDeck(slug, deckType)
        if (!deck) {
          setError('Deck not found')
          setLoading(false)
          return
        }
        await openDeck(deck, result, slug)
      } else {
        setAuthResult(result)
        setPickerMode(true)
        setLoading(false)
      }
    } catch {
      setError('Connection error')
      setLoading(false)
    }
  }, [code, loading, workshopSlug, requestedDeckType, openDeck])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }

      if (pickerMode && authResult && resolvedSlug) {
        const decks = getDecksByWorkshop(resolvedSlug)
        if (e.key === 'ArrowUp') {
          e.preventDefault()
          setPickerIndex((i) => Math.max(0, i - 1))
        } else if (e.key === 'ArrowDown') {
          e.preventDefault()
          setPickerIndex((i) => Math.min(decks.length - 1, i + 1))
        } else if (e.key === 'Enter') {
          e.preventDefault()
          const deck = decks[pickerIndex]
          if (deck) openDeck(deck, authResult, resolvedSlug)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose, pickerMode, resolvedSlug, authResult, pickerIndex, openDeck])

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit]
  )

  const gate = (
    <Overlay data-gate-overlay="" onPress={onClose}>
      <YStack
        width="100%"
        maxWidth={480}
        padding="$8"
        gap="$5"
        alignItems="center"
        onPress={(e: any) => e.stopPropagation()}
      >
        {/* Header */}
        <YStack alignItems="center" gap="$2">
          <Text
            fontFamily="$mono"
            color="$orangePrimary"
            textTransform="uppercase"
            letterSpacing={3}
            style={{ fontSize: '11px' }}
          >
            Secure Access Request
          </Text>
        </YStack>

        {/* Code input or picker */}
        {!pickerMode ? (
          <YStack width="100%" gap="$3" alignItems="center">
            <CodeInput
              ref={inputRef as any}
              data-gate-input=""
              secureTextEntry
              placeholder="enter access code"
              placeholderTextColor={'#666666' as any}
              value={code}
              onChangeText={setCode}
              onKeyDown={handleInputKeyDown as any}
              autoComplete="off"
              {...(error ? { borderColor: '#EF4444' } : {})}
            />
            {error && (
              <Text
                fontFamily="$mono"
                color="#EF4444"
                style={{ fontSize: '13px' }}
              >
                {error}
              </Text>
            )}
            {loading && (
              <Text
                fontFamily="$mono"
                color="$textMuted"
                style={{ fontSize: '13px' }}
              >
                verifying...
              </Text>
            )}
          </YStack>
        ) : (
          <YStack width="100%" gap="$3">
            {authResult?.attendeeName && (
              <Text
                fontFamily="$mono"
                color="$orangePrimary"
                textAlign="center"
                style={{ fontSize: '14px' }}
              >
                Welcome, {authResult.attendeeName}
              </Text>
            )}
            <Text
              fontFamily="$mono"
              color="$textMuted"
              textAlign="center"
              style={{ fontSize: '12px' }}
            >
              Select a deck
            </Text>
            {getDecksByWorkshop(resolvedSlug).map((deck, i) => (
              <PickerCard
                key={deck.id}
                active={i === pickerIndex}
                onPress={() => {
                  setPickerIndex(i)
                  if (authResult)
                    openDeck(deck, authResult, resolvedSlug)
                }}
              >
                <Text
                  fontFamily="$mono"
                  color={
                    i === pickerIndex ? '$orangePrimary' : '$textSecondary'
                  }
                  style={{ fontSize: '14px' }}
                >
                  {deck.label}
                </Text>
                <Text
                  fontFamily="$mono"
                  color="$textMuted"
                  style={{ fontSize: '11px' }}
                >
                  {deck.slides.length} slides
                </Text>
              </PickerCard>
            ))}
          </YStack>
        )}

        {/* Hints */}
        <Text fontFamily="$mono" color="$textMuted" style={{ fontSize: '10px' }}>
          {pickerMode
            ? '↑↓ select  ·  enter confirm  ·  esc close'
            : 'enter to submit  ·  esc to close'}
        </Text>
      </YStack>
    </Overlay>
  )

  return createPortal(gate, document.body)
}
